import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Gauge, Loader2, MessageSquare, Info } from "lucide-react";
import api from "src/utils/api";
import { useActiveUser } from "src/hooks/useActiveUser";
import chatWebSocketService from "src/services/websocket";
import { getPlanNameFromSubscription } from "src/utils/subscription";
import { useAssistantContext } from "src/contexts/AssistantContext";

// Tokens per conversation (average)
const TOKENS_PER_CONVERSATION = 75000;

const BotStatusMonitor = ({ className = "", compact = false }) => {
  const [data, setData] = useState({
    loading: true,
    error: false,
    planName: "Carregando...",
    usagePercentage: 0,
    remainingTokens: 0,
    totalTokens: 0,
    consumedTokens: 0,
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const wsConnectedRef = useRef(false);

  const navigate = useNavigate();
  const { user } = useActiveUser();
  const { selectedAssistant } = useAssistantContext();

  // Handle real-time token updates from WebSocket
  const handleTokenUpdate = useCallback((payload) => {
    if (!payload) return;

    console.log('TOKEN USAGE UPDATE: Received real-time update', payload);

    const remaining = Number(payload.remainingTokens || 0);
    const consumed = Number(payload.montlyTokensConsumed || 0);
    const total = Number(payload.totalTokens || remaining + consumed);
    const percentage = payload.usagePercentage ?? (total > 0 ? Math.round((consumed / total) * 100) : 0);

    setData(prev => ({
      ...prev,
      usagePercentage: percentage,
      remainingTokens: remaining,
      totalTokens: total,
      consumedTokens: consumed,
    }));
  }, []);

  // Connect to WebSocket for real-time updates
  useEffect(() => {
    const partnerId = user?.id || user?.partnerId;
    let isMounted = true; // Track if component is still mounted

    if (!partnerId) return;

    // Initialize WebSocket if not already
    chatWebSocketService.initialize();

    // Small delay to ensure WebSocket is ready
    const connectTimer = setTimeout(() => {
      // Cancel if component unmounted during delay
      if (!isMounted) return;

      console.log('TOKEN USAGE: Connecting to TokenUsageChannel', partnerId);

      chatWebSocketService.connectTokenUsage(partnerId, selectedAssistant?.id || null, {
        onTokenUsageUpdated: (payload) => {
          // Only update if still mounted
          if (isMounted) {
            handleTokenUpdate(payload);
          }
        },
        onConnected: () => {
          if (isMounted) {
            console.log('TOKEN USAGE: WebSocket connected');
            wsConnectedRef.current = true;
          }
        },
        onDisconnected: () => {
          console.log('TOKEN USAGE: WebSocket disconnected');
          wsConnectedRef.current = false;
        }
      });
    }, 1000);

    return () => {
      isMounted = false;
      clearTimeout(connectTimer);
      // Always disconnect on cleanup to prevent memory leaks
      chatWebSocketService.disconnectTokenUsage();
      wsConnectedRef.current = false;
    };
  }, [user?.id, user?.partnerId, selectedAssistant?.id, handleTokenUpdate]);

  // Fetch token usage data (initial load)
  const fetchTokenData = useCallback(async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: false }));

      // Fetch both endpoints in parallel
      const [usageResponse, subscriptionResponse] = await Promise.all([
        api.get('/partners/montly_usage_history'),
        api.get('/partners/payment_subscriptions/last_active_subscription').catch(() => null)
      ]);

      const usage = usageResponse.data?.data?.usageStatistics || {};
      const subscription = subscriptionResponse?.data?.data?.attributes || {};

      const remaining = Number(usage.remainingTokens || 0);
      const consumed = Number(usage.montlyTokensConsumed || 0);
      const total = remaining + consumed;
      const percentage = total > 0 ? Math.round((consumed / total) * 100) : 0;

      setData({
        loading: false,
        error: false,
        planName: getPlanNameFromSubscription(subscription),
        usagePercentage: percentage,
        remainingTokens: remaining,
        totalTokens: total,
        consumedTokens: consumed,
      });
    } catch (error) {
      console.error('Error fetching token data:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: true,
      }));
    }
  }, []);

  useEffect(() => {
    fetchTokenData();
  }, [fetchTokenData]);

  // Navigate to finance page on click
  const handleClick = useCallback(() => {
    navigate('/financeiro');
  }, [navigate]);

  // Get gradient color based on usage percentage
  const getProgressColor = (percentage) => {
    if (percentage <= 50) {
      return "from-emerald-400 to-emerald-500";
    } else if (percentage <= 80) {
      return "from-amber-400 to-amber-500";
    } else {
      return "from-rose-400 to-rose-500";
    }
  };


  // Format number compactly for large values (e.g., 100M, 4,3M)
  const formatNumberCompact = (num) => {
    if (num >= 1000000) {
      const millions = num / 1000000;
      // Show decimal only if needed and not too long
      if (millions >= 100) {
        return `${Math.round(millions)}M`;
      }
      return `${millions.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}K`;
    }
    return num.toLocaleString('pt-BR');
  };

  const progressColor = getProgressColor(data.usagePercentage);
  const isCritical = data.usagePercentage >= 100;

  // Calculate conversations
  const totalConversations = Math.floor(data.totalTokens / TOKENS_PER_CONVERSATION);
  const consumedConversations = Math.floor(data.consumedTokens / TOKENS_PER_CONVERSATION);
  const remainingConversations = Math.floor(data.remainingTokens / TOKENS_PER_CONVERSATION);

  // Inline keyframes for smooth fade animation
  const fadeKeyframes = `
    @keyframes tooltipFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `;

  // Compact version for mobile
  if (compact) {
    return (
      <button
        type="button"
        aria-label="Ver consumo de tokens e financeiro"
        className={`
          token-monitor-compact flex items-center gap-1.5 px-2 py-1
          bg-white/90 backdrop-blur-lg rounded-full
          border border-slate-200 shadow-sm
          cursor-pointer
          ${className}
        `}
        onClick={handleClick}
      >
        {/* Coins Icon - smaller */}
        <div className="token-icon flex items-center justify-center h-5 w-5 rounded-full bg-slate-100">
          {data.loading ? (
            <Loader2 className="h-3 w-3 text-slate-500 animate-spin" />
          ) : (
            <Gauge className="h-3 w-3 text-slate-600" />
          )}
        </div>

        {/* Progress Bar - shorter */}
        <div className="token-progress relative w-10 h-1 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`
              token-progress-fill absolute inset-y-0 left-0 rounded-full
              bg-gradient-to-r ${progressColor}
              transition-all duration-500 ease-out
              ${isCritical ? 'animate-pulse' : ''}
            `}
            data-usage-tone={data.usagePercentage > 80 ? 'critical' : data.usagePercentage > 50 ? 'warning' : 'normal'}
            style={{ width: `${Math.min(data.usagePercentage, 100)}%` }}
          />
        </div>

        {/* Percentage - smaller */}
        <span className={`
          token-percentage text-[10px] font-semibold
          ${data.usagePercentage >= 80 ? 'text-rose-600' :
            data.usagePercentage >= 50 ? 'text-amber-600' : 'text-slate-600'}
        `}>
          {data.loading ? "--" : `${data.usagePercentage}%`}
        </span>
      </button>
    );
  }

  return (
    <div className="relative">
      <style>{fadeKeyframes}</style>
      {/* Main Pill */}
      <button
        type="button"
        aria-label={`Ver consumo de tokens e financeiro: ${data.loading ? 'carregando' : `${data.planName}, ${data.usagePercentage}% utilizados`}`}
        className={`
          relative flex items-center gap-2 px-3 py-1.5
          bg-white/90 backdrop-blur-lg rounded-full
          border border-slate-200 shadow-sm
          hover:bg-white hover:shadow-md hover:border-slate-300
          transition-all duration-300 cursor-pointer
          group
          ${className}
        `}
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
      >
        {/* Coins Icon */}
        <div className="token-icon flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 group-hover:bg-slate-200 transition-colors">
          {data.loading ? (
            <Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" />
          ) : (
            <Gauge className="h-3.5 w-3.5 text-slate-600" />
          )}
        </div>

        {/* Plan Name */}
        <span className="token-plan-name text-xs font-medium text-slate-600 whitespace-nowrap">
          {data.loading ? "..." : data.planName}
        </span>

        {/* Progress Bar */}
        <div className="token-progress relative w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`
              token-progress-fill absolute inset-y-0 left-0 rounded-full
              bg-gradient-to-r ${progressColor}
              transition-all duration-500 ease-out
              ${isCritical ? 'animate-pulse' : ''}
            `}
            data-usage-tone={data.usagePercentage > 80 ? 'critical' : data.usagePercentage > 50 ? 'warning' : 'normal'}
            style={{ width: `${Math.min(data.usagePercentage, 100)}%` }}
          />
        </div>

        {/* Percentage */}
        <span className={`
          token-percentage text-xs font-semibold min-w-[32px] text-right
          ${data.usagePercentage >= 80 ? 'text-rose-600' :
            data.usagePercentage >= 50 ? 'text-amber-600' : 'text-slate-600'}
        `}>
          {data.loading ? "--" : `${data.usagePercentage}%`}
        </span>

        {/* Hover gradient overlay */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </button>

      {/* Rich Tooltip */}
      {showTooltip && !data.loading && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 w-72 p-4 bg-white rounded-xl shadow-xl border border-slate-200"
          style={{
            animation: 'tooltipFadeIn 300ms ease-in-out forwards',
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* Arrow */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-slate-200 rotate-45" />

          {/* Header */}
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-100">
            <Coins className="h-5 w-5 text-slate-600" />
            <span className="font-semibold text-slate-700">Consumo de Tokens</span>
          </div>

          {/* Tokens Section */}
          <div className="flex flex-col gap-1 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">Consumidos:</span>
              <span className="text-sm font-semibold text-amber-600">{formatNumberCompact(data.consumedTokens)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">Total:</span>
              <span className="text-sm font-semibold text-slate-700">{formatNumberCompact(data.totalTokens)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">Restantes:</span>
              <span className={`text-sm font-semibold ${data.remainingTokens > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {formatNumberCompact(data.remainingTokens)}
              </span>
            </div>
          </div>

          {/* Conversations Section */}
          <div className="bg-slate-50 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">Equivalente em Conversas</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Disponíveis:</span>
              <span className={`text-lg font-bold ${remainingConversations > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {remainingConversations}
              </span>
            </div>
            <div className="flex justify-between items-center text-xs text-slate-400">
              <span>Usadas: {consumedConversations}</span>
              <span>Total: {totalConversations}</span>
            </div>
          </div>

          {/* Info Section */}
          <div className="flex gap-2 p-2 bg-blue-50 rounded-lg">
            <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 leading-relaxed normal-case">
              <strong>Tokens</strong> são unidades que medem o uso da IA.
              Cada mensagem enviada e recebida consome tokens.
              Uma conversa consome em média cerca de 75 mil tokens.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BotStatusMonitor;
